import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { KeyRound, Mail, Loader2, Copy, CheckCircle2, AlertTriangle } from 'lucide-react';
import { copiarTexto } from '../dashboard/rotina';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Redefinir senha pelo e-mail (Adnéia, 20/10/2026), só para a gestão (admin).
// O ELO manda à pessoa um link que vale 24h e funciona uma vez só; ela cria a senha
// nova sozinha e ninguém vê a senha dela. Backend: routes/senha.py.
const SenhasEquipe = () => {
  const { getAuthHeader } = useAuth();
  const [pessoas, setPessoas] = useState(null); // null = carregando
  const [erro, setErro] = useState('');
  const [aberto, setAberto] = useState(null); // id da pessoa com o formulário aberto
  const [para, setPara] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState({}); // id -> resposta do envio

  const carregar = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/auth/redefinir-senha/equipe`, { headers: getAuthHeader() });
      setPessoas(r.data?.pessoas || []);
      setErro('');
    } catch (e) {
      setPessoas([]);
      setErro(e.response?.data?.detail || 'Não consegui carregar a equipe.');
    }
  }, [getAuthHeader]);

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const abrir = (p) => {
    setAberto(p.id);
    setPara(p.email);
  };

  const mandar = async (p) => {
    setEnviando(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/auth/redefinir-senha/enviar`,
        { user_id: p.id, enviar_para: para },
        { headers: getAuthHeader() }
      );
      const hora = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      setResultado((prev) => ({ ...prev, [p.id]: { ...r.data, hora } }));
      setAberto(null);
      if (r.data?.enviado) {
        toast.success(`Link mandado para ${r.data.para}`);
      } else {
        toast.warning('O e-mail não saiu. Copie o link e mande à pessoa.');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Não consegui mandar o link.');
    } finally {
      setEnviando(false);
    }
  };

  const copiar = (link) => {
    copiarTexto(
      link,
      () => toast.success('Link copiado'),
      () => toast.error('Não consegui copiar. Selecione o link e copie.')
    );
  };

  return (
    <Card data-testid="senhas-equipe">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <KeyRound className="h-5 w-5" />
          Senhas da equipe
        </CardTitle>
        <CardDescription>
          Quem esqueceu a senha recebe por e-mail um link para criar uma nova. O link vale 24 horas
          e funciona uma vez só. Até ser usado, a senha atual continua valendo. Ninguém vê a senha nova.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {pessoas === null && (
          <p className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Carregando a equipe...
          </p>
        )}
        {erro && <p className="text-sm text-red-600 dark:text-red-400">{erro}</p>}
        {pessoas && pessoas.length === 0 && !erro && (
          <p className="text-sm text-muted-foreground">Ninguém na equipe além de você.</p>
        )}
        {(pessoas || []).map((p) => {
          const res = resultado[p.id];
          return (
            <div key={p.id} className="rounded-md border p-3 space-y-2" data-testid={`senha-pessoa-${p.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <p className="font-medium truncate">
                    {p.name}
                    {!p.ativo && (
                      <span className="ml-2 text-xs rounded bg-slate-200 dark:bg-slate-800 px-1.5 py-0.5 text-muted-foreground">
                        desativada
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-muted-foreground truncate">{p.email}</p>
                </div>
                {aberto !== p.id && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!p.ativo || enviando}
                    onClick={() => abrir(p)}
                    data-testid={`btn-link-senha-${p.id}`}
                  >
                    <Mail className="h-4 w-4 mr-2" />
                    Mandar link de nova senha
                  </Button>
                )}
              </div>

              {aberto === p.id && (
                <div className="space-y-2 rounded-md bg-slate-50 dark:bg-slate-900 p-3">
                  <Label htmlFor={`para-${p.id}`}>Mandar para</Label>
                  <Input
                    id={`para-${p.id}`}
                    type="email"
                    value={para}
                    onChange={(e) => setPara(e.target.value)}
                    disabled={enviando}
                  />
                  <p className="text-xs text-muted-foreground">
                    Só e-mail da casa (@weconnect360.com.br ou @wct360.com.br). Um link novo cancela o anterior.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => mandar(p)} disabled={enviando || !para.trim()}>
                      {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
                      Mandar
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setAberto(null)} disabled={enviando}>
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {res && res.enviado && (
                <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  Link mandado para {res.para} às {res.hora}. Vale {res.validade_horas} horas.
                </p>
              )}
              {res && !res.enviado && res.link && (
                <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 p-3">
                  <p className="text-sm text-amber-900 dark:text-amber-100 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    O e-mail não saiu. Mande este link a {p.name} (vale {res.validade_horas} horas, uma vez só):
                  </p>
                  <div className="flex gap-2">
                    <Input readOnly value={res.link} onFocus={(e) => e.target.select()} className="text-xs" />
                    <Button size="sm" variant="outline" onClick={() => copiar(res.link)}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copiar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default SenhasEquipe;
